import type { CSSProperties } from "react";
import type { LessonWorkspaceMode } from "../auth/api-client";
import { fallbackGradient } from "./lessonIcons";

export function isAdvancedLessonMode(mode?: LessonWorkspaceMode | null): boolean {
  return mode === "advanced";
}

function parseHex(color: string): { r: number; g: number; b: number } | null {
  const hex = color.trim().replace("#", "");
  if (hex.length === 3) {
    return {
      r: parseInt(hex[0]! + hex[0]!, 16),
      g: parseInt(hex[1]! + hex[1]!, 16),
      b: parseInt(hex[2]! + hex[2]!, 16),
    };
  }
  if (hex.length === 6) {
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  }
  return null;
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l: l * 100 };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;
  return { h: h * 360, s: s * 100, l: l * 100 };
}

function hslToHex(h: number, s: number, l: number): string {
  const sn = Math.max(0, Math.min(100, s)) / 100;
  const ln = Math.max(0, Math.min(100, l)) / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = ln - c / 2;
  let rp = 0;
  let gp = 0;
  let bp = 0;
  if (h < 60) {
    rp = c;
    gp = x;
  } else if (h < 120) {
    rp = x;
    gp = c;
  } else if (h < 180) {
    gp = c;
    bp = x;
  } else if (h < 240) {
    gp = x;
    bp = c;
  } else if (h < 300) {
    rp = x;
    bp = c;
  } else {
    rp = c;
    bp = x;
  }
  const toByte = (v: number) => Math.round((v + m) * 255);
  const hex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${hex(toByte(rp))}${hex(toByte(gp))}${hex(toByte(bp))}`;
}

function hexToRgba(hex: string, alpha: number): string {
  const rgb = parseHex(hex);
  if (!rgb) return `rgba(107, 163, 54, ${alpha})`;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

function baseHexFromColor(baseColor: string | null | undefined, fallbackSeed: string): string {
  const trimmed = baseColor?.trim();
  if (trimmed?.startsWith("#")) return trimmed;
  const gradient = fallbackGradient(fallbackSeed);
  return gradient.match(/#[0-9a-fA-F]{3,8}/)?.[0] ?? "#1b4d2e";
}

export function buildEpicLessonGradient(baseHex: string): string {
  const rgb = parseHex(baseHex) ?? { r: 27, g: 77, b: 46 };
  const { h, s, l } = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const deep = hslToHex(h, Math.min(s + 6, 95), Math.max(l - 16, 8));
  const mid = baseHex;
  const bright = hslToHex(h, Math.min(s + 14, 100), Math.min(l + 18, 90));
  const flare = hslToHex((h + 38) % 360, Math.min(s + 22, 92), Math.min(l + 12, 82));
  return `linear-gradient(145deg, ${deep} 0%, ${mid} 34%, ${bright} 68%, ${flare} 100%)`;
}

export function buildEpicBorderGradient(baseHex: string): string {
  const rgb = parseHex(baseHex) ?? { r: 27, g: 77, b: 46 };
  const { h, s, l } = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const shimmer = hslToHex((h + 48) % 360, 88, Math.min(l + 32, 78));
  const glow = hslToHex(h, Math.min(s + 28, 100), Math.min(l + 24, 92));
  const core = baseHex;
  const accent = hslToHex((h + 195) % 360, 72, Math.min(l + 14, 80));
  const shadow = hslToHex(h, Math.min(s + 10, 95), Math.max(l - 20, 10));
  return `linear-gradient(135deg, ${shimmer} 0%, ${glow} 18%, ${core} 42%, ${accent} 70%, ${shadow} 100%)`;
}

export interface LessonEpicVisualStyle {
  readonly frameClassName: string;
  readonly frameStyle: CSSProperties;
  readonly innerBackground: string;
}

export function lessonEpicVisualStyle(opts: {
  workspaceMode?: LessonWorkspaceMode | null;
  baseColor?: string | null;
  fallbackSeed: string;
}): LessonEpicVisualStyle | null {
  if (!isAdvancedLessonMode(opts.workspaceMode)) return null;
  const baseHex = baseHexFromColor(opts.baseColor, opts.fallbackSeed);
  const innerBackground = buildEpicLessonGradient(baseHex);
  const borderGradient = buildEpicBorderGradient(baseHex);
  const rgb = parseHex(baseHex) ?? { r: 27, g: 77, b: 46 };
  const { h, l } = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const shimmerHex = hslToHex((h + 48) % 360, 88, Math.min(l + 28, 76));
  return {
    frameClassName: "lesson-visual-epic-frame",
    frameStyle: {
      "--lesson-epic-border": borderGradient,
      "--lesson-epic-glow": hexToRgba(baseHex, 0.48),
      "--lesson-epic-shimmer": hexToRgba(shimmerHex, 0.42),
    } as CSSProperties,
    innerBackground,
  };
}

export function itemRowEpicFrameStyle(
  baseColor: string | null | undefined,
  fallbackSeed: string
): CSSProperties {
  const baseHex = baseHexFromColor(baseColor, fallbackSeed);
  const borderGradient = buildEpicBorderGradient(baseHex);
  const rgb = parseHex(baseHex) ?? { r: 27, g: 77, b: 46 };
  const { h, l } = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const shimmerHex = hslToHex((h + 48) % 360, 88, Math.min(l + 28, 76));
  return {
    "--lesson-epic-border": borderGradient,
    "--lesson-epic-glow": hexToRgba(baseHex, 0.42),
    "--lesson-epic-shimmer": hexToRgba(shimmerHex, 0.35),
  } as CSSProperties;
}

export function lessonCardEpicWrapStyle(
  epic: LessonEpicVisualStyle
): { className: string; style: CSSProperties } {
  return {
    className: "teacher-lesson-card-wrap--advanced",
    style: {
      ...epic.frameStyle,
      "--lesson-card-bg": epic.innerBackground,
    } as CSSProperties,
  };
}
